import { useState, useCallback, useRef, useEffect, useMemo } from 'react';

import { buildRangeKey } from '../definition-tab.constants';
import type { RangeLike } from '../definition-tab.constants';

interface MatrixCellValue {
  id?: number | null;
  amount: number;
  discount: number;
}

type MatrixMap = Map<string, MatrixCellValue>;

interface CalculationRow {
  id?: number | null;
  academicRankId?: number | null;
  range?: RangeLike;
  amount?: number;
  discount?: number;
  nonAid?: boolean | null;
  nonAgi?: boolean | null;
}

const DEFAULT_CELL: MatrixCellValue = { id: null, amount: 0, discount: 0 };

const buildMatrixFromCalculation = (
  calculation: CalculationRow[] = [],
  ranges: RangeLike[],
  rankIds: Array<string | number>
): MatrixMap => {
  const matrix: MatrixMap = new Map();

  rankIds.forEach((rankId) => {
    ranges.forEach((range) => {
      const key = `${rankId}_${buildRangeKey(range)}`;

      matrix.set(key, { ...DEFAULT_CELL });
    });
  });

  calculation.forEach((row) => {
    if (!row.range || row.academicRankId == null) return;

    const rangeKey = buildRangeKey({
      ...row.range,
      nonAid: row.nonAid ?? undefined,
      nonAgi: row.nonAgi ?? undefined,
    });
    const key = `${row.academicRankId}_${rangeKey}`;

    if (matrix.has(key)) {
      matrix.set(key, {
        id: row.id ?? null,
        amount: row.amount ?? 0,
        discount: row.discount ?? 0,
      });
    }
  });

  return matrix;
};

const rebuildMatrix = (
  prevMatrix: MatrixMap,
  ranges: RangeLike[],
  rankIds: Array<string | number>
): MatrixMap => {
  const next: MatrixMap = new Map();

  rankIds.forEach((rankId) => {
    ranges.forEach((range) => {
      const key = `${rankId}_${buildRangeKey(range)}`;
      const existing = prevMatrix.get(key);

      next.set(key, existing ? { ...existing } : { ...DEFAULT_CELL });
    });
  });

  return next;
};

const cloneMatrix = (matrix: MatrixMap): MatrixMap =>
  new Map([...matrix.entries()].map(([k, v]) => [k, { ...v }]));

const serializeRanges = (ranges: RangeLike[]): string =>
  ranges.map((r) => buildRangeKey(r)).join('|');

interface UseAwardCalculationDraftParams {
  regime?: { id?: string | number; calculation?: unknown[] } & Record<
    string,
    unknown
  >;
  ranges?: RangeLike[];
  academicRanks?: Array<{ id: string | number; rank: number }>;
}

const useAwardCalculationDraft = ({
  regime,
  ranges = [],
  academicRanks = [],
}: UseAwardCalculationDraftParams) => {
  const rankIds = useMemo(
    () => academicRanks.map((ar) => ar.id),
    [academicRanks]
  );

  const rankIdToDisplayNumber = useMemo(() => {
    const map = new Map();

    academicRanks.forEach((ar) => map.set(ar.id, ar.rank));

    return map;
  }, [academicRanks]);

  const initialRef = useRef<MatrixMap | null>(null);
  const prevRangesRef = useRef<string>('');
  const prevRankIdsRef = useRef<string>('');
  const needsFullRebuildRef = useRef<boolean>(true);
  const regimeRef = useRef(regime);

  regimeRef.current = regime;

  const [matrix, setMatrix] = useState<MatrixMap>(() => new Map());
  const [regimeVersion, setRegimeVersion] = useState<number>(0);

  useEffect(() => {
    needsFullRebuildRef.current = true;
    initialRef.current = null;
    prevRangesRef.current = '';
    prevRankIdsRef.current = '';
    setRegimeVersion((v) => v + 1);
  }, [regime?.id]);

  useEffect(() => {
    const serializedRanges = serializeRanges(ranges);
    const serializedRankIds = rankIds.join(',');
    const rangesChanged = serializedRanges !== prevRangesRef.current;
    const rankIdsChanged = serializedRankIds !== prevRankIdsRef.current;

    if (!rangesChanged && !rankIdsChanged) return;

    prevRangesRef.current = serializedRanges;
    prevRankIdsRef.current = serializedRankIds;

    if (needsFullRebuildRef.current || rankIdsChanged) {
      if (!ranges.length || !rankIds.length) {
        initialRef.current = new Map();
        setMatrix(new Map());

        return;
      }

      const m = buildMatrixFromCalculation(
        regimeRef.current?.calculation as CalculationRow[] | undefined,
        ranges,
        rankIds
      );

      initialRef.current = cloneMatrix(m);
      setMatrix(m);
    } else {
      if (initialRef.current) {
        initialRef.current = rebuildMatrix(initialRef.current, ranges, rankIds);
      }

      setMatrix((prev) => rebuildMatrix(prev, ranges, rankIds));
    }
  }, [ranges, rankIds, regimeVersion]);

  const updateCell = useCallback(
    (
      rankId: string | number,
      rangeKey: string,
      field: string,
      value: number
    ): void => {
      setMatrix((prev) => {
        const key = `${rankId}_${rangeKey}`;
        const cell = prev.get(key);

        if (!cell) return prev;

        needsFullRebuildRef.current = false;

        const next = new Map(prev);

        next.set(key, { ...cell, [field]: value });

        return next;
      });
    },
    []
  );

  const getCell = useCallback(
    (rankId: string | number, rangeKey: string) =>
      matrix.get(`${rankId}_${rangeKey}`) ?? DEFAULT_CELL,
    [matrix]
  );

  const resetDraft = useCallback(() => {
    if (!initialRef.current) return;

    setMatrix(cloneMatrix(initialRef.current));
  }, []);

  const toCalculationPayload = useCallback(() => {
    const entries: Array<{
      id?: number;
      academicRankId: number;
      range: { from: number | null; to: number | null };
      amount: number;
      discount: number;
      nonAid?: boolean;
      nonAgi?: boolean;
    }> = [];

    for (const [key, cell] of matrix) {
      const separatorIdx = key.indexOf('_');
      const academicRankId = Number.parseInt(key.slice(0, separatorIdx), 10);
      let rangeStr = key.slice(separatorIdx + 1);

      let nonAid: boolean | undefined;
      let nonAgi: boolean | undefined;

      if (rangeStr.endsWith('_nonagi')) {
        rangeStr = rangeStr.slice(0, -'_nonagi'.length);
        nonAgi = true;
      } else if (rangeStr.endsWith('_pell')) {
        rangeStr = rangeStr.slice(0, -'_pell'.length);
        nonAid = false;
      } else if (rangeStr === 'null_null') {
        nonAid = true;
      }

      const [fromStr, toStr] = rangeStr.split('_');

      const entry: {
        id?: number;
        academicRankId: number;
        range: { from: number | null; to: number | null };
        amount: number;
        discount: number;
        nonAid?: boolean;
        nonAgi?: boolean;
      } = {
        academicRankId,
        range: {
          from: fromStr === 'null' ? null : Number.parseInt(fromStr, 10),
          to: toStr === 'null' ? null : Number.parseInt(toStr, 10),
        },
        amount: cell.amount,
        discount: cell.discount,
      };

      if (nonAid !== undefined) {
        entry.nonAid = nonAid;
      }

      if (nonAgi !== undefined) {
        entry.nonAgi = nonAgi;
      }

      if (cell.id != null) {
        entry.id = cell.id;
      }

      entries.push(entry);
    }

    return entries;
  }, [matrix]);

  const markAsSaved = useCallback(() => {
    initialRef.current = cloneMatrix(matrix);
    setMatrix((m) => new Map(m));
  }, [matrix]);

  const hasChanges = useMemo(() => {
    if (!initialRef.current) return false;
    if (matrix.size !== initialRef.current.size) return true;

    for (const [key, cell] of matrix) {
      const initial = initialRef.current.get(key);

      if (!initial) return true;
      if (
        cell.amount !== initial.amount ||
        cell.discount !== initial.discount
      ) {
        return true;
      }
    }

    return false;
  }, [matrix]);

  return {
    matrix,
    rankIds,
    rankIdToDisplayNumber,
    getCell,
    updateCell,
    hasChanges,
    resetDraft,
    toCalculationPayload,
    markAsSaved,
  };
};

export default useAwardCalculationDraft;
